const EVIDENCE_PRIMARY_FIELDS = [
  'item_id',
  'example_id',
  'dialog_id',
  'id',
  'score',
  'reason',
  'explanation',
  'summary',
  'user_message',
  'input',
  'negative_example',
  'positive_example',
  'expected',
  'predicted',
  'prediction',
  'output',
  'reference_answer',
  'ground_truth',
  'assistant_message',
  'comment',
  'comments',
  'message',
  'text',
] as const;

const EVIDENCE_FIELD_LABELS: Record<string, string> = {
  negative_example: 'Negative example',
  positive_example: 'Positive example',
  expected: 'Expected',
  predicted: 'Predicted',
  prediction: 'Predicted',
  reference_answer: 'Expected',
  ground_truth: 'Expected',
  output: 'Predicted',
  comments: 'Comments',
  comment: 'Comments',
  user_message: 'User message',
  input: 'Input',
};

const EVIDENCE_FIELD_HELP: Record<string, string> = {
  negative_example:
    'The assistant reply stored in the dataset with a dislike reaction. Shown for context only — it is not the answer being scored.',
  positive_example:
    'The assistant reply stored in the dataset with a like reaction.',
  expected:
    'Reference answer regenerated by the teacher model. The score measures how close Predicted is to this reference.',
  reference_answer:
    'Reference answer regenerated by the teacher model. The score measures how close Predicted is to this reference.',
  ground_truth:
    'Reference answer regenerated by the teacher model. The score measures how close Predicted is to this reference.',
  predicted:
    'Answer produced by the agent under evaluation.',
  prediction:
    'Answer produced by the agent under evaluation.',
  output:
    'Answer produced by the agent under evaluation.',
  comments:
    'Annotator comments for this dataset item, if any.',
  comment:
    'Annotator comments for this dataset item, if any.',
};

export const EVALUATION_EVIDENCE_INTRO =
  'Each card is one dataset item. Negative example is the disliked reply from the dataset. Expected is a separate reference from the teacher model. Predicted is the agent output. Similar text in Negative example and Expected is normal — they come from different sources.';

const EVIDENCE_ALWAYS_SHOW_WHEN_EMPTY = new Set(['comment', 'comments']);

const EVIDENCE_META_FIELDS = ['agent_name', 'metric_id', 'metric', 'split', 'label'] as const;

const EVIDENCE_HIDDEN_FIELDS = new Set([
  'agent_id',
  'embedding',
  'embeddings',
  'token_ids',
  'tokens',
  'raw',
  'raw_payload',
  'internal',
  'trace_id',
  'request_id',
  'metadata',
]);

export type EvidenceFieldGroup = 'primary' | 'meta' | 'technical';

export interface ClassifiedEvidenceField {
  key: string;
  label: string;
  help?: string;
  value: unknown;
  group: EvidenceFieldGroup;
}

function formatFieldLabel(key: string): string {
  return EVIDENCE_FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function evidenceFieldHelp(key: string): string | undefined {
  return EVIDENCE_FIELD_HELP[key];
}

function isHiddenEvidenceKey(key: string): boolean {
  return EVIDENCE_HIDDEN_FIELDS.has(key) || key.startsWith('_');
}

export function classifyEvidenceFields(record: Record<string, unknown>): ClassifiedEvidenceField[] {
  const entries = Object.entries(record).filter(([key, value]) => {
    if (EVIDENCE_ALWAYS_SHOW_WHEN_EMPTY.has(key)) return true;
    return value != null && value !== '' && !isHiddenEvidenceKey(key);
  });

  const orderIndex = (key: string): number => {
    const primaryIdx = EVIDENCE_PRIMARY_FIELDS.indexOf(key as (typeof EVIDENCE_PRIMARY_FIELDS)[number]);
    if (primaryIdx >= 0) return primaryIdx;
    const metaIdx = EVIDENCE_META_FIELDS.indexOf(key as (typeof EVIDENCE_META_FIELDS)[number]);
    if (metaIdx >= 0) return EVIDENCE_PRIMARY_FIELDS.length + metaIdx;
    return EVIDENCE_PRIMARY_FIELDS.length + EVIDENCE_META_FIELDS.length + 1;
  };

  return entries
    .map(([key, value]) => {
      let group: EvidenceFieldGroup = 'technical';
      if ((EVIDENCE_PRIMARY_FIELDS as readonly string[]).includes(key)) {
        group = 'primary';
      } else if ((EVIDENCE_META_FIELDS as readonly string[]).includes(key)) {
        group = 'meta';
      }
      return {
        key,
        label: formatFieldLabel(key),
        help: evidenceFieldHelp(key),
        value,
        group,
      };
    })
    .sort((a, b) => {
      const orderDiff = orderIndex(a.key) - orderIndex(b.key);
      if (orderDiff !== 0) return orderDiff;
      return a.key.localeCompare(b.key);
    });
}

export function formatEvidenceScalar(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

export function isMultilineEvidenceValue(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.length > 80 || value.includes('\n');
  }
  return typeof value === 'object' && value !== null;
}

export function evidenceCardTitle(record: Record<string, unknown>, index: number): string {
  for (const key of ['item_id', 'example_id', 'dialog_id', 'id']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return `Example ${index + 1}`;
}

export function evidenceCardScore(record: Record<string, unknown>): string | null {
  const score = record.score;
  if (score == null) return null;
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(4);
}
